import React from "react";
import { TopBar } from "@/components/TopBar";
import { EmptyState } from "@/components/EmptyState";
import { ProductImage } from "@/components/ProductImage";
import { toneFor } from "@/components/ProductCard";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { useCompare } from "@/lib/compare";
import { useBackButton } from "@/lib/hooks";
import { formatUzs } from "@/lib/format";
import { flyToCart } from "@/lib/motion";
import { haptic } from "@/lib/telegram";

/** Side-by-side comparison of two products: every attribute either has, price, rating. */
export function ComparePage() {
  const { t, locale } = useI18n();
  const { goBack, navigate } = useRouter();
  const { getProduct } = useCatalog();
  const { addItem } = useCart();
  const compare = useCompare();
  useBackButton(goBack);

  const products = compare.ids.map((id) => getProduct(id)).filter((p): p is NonNullable<typeof p> => !!p);

  if (products.length < 2) {
    return (
      <>
        <TopBar title={t("compare.title")} onBack={goBack} />
        <main className="page page--no-nav">
          <EmptyState art="search" title={t("compare.emptyTitle")} hint={t("compare.emptyHint")} action={<button className="btn btn--primary" onClick={() => navigate({ name: "catalog", category: "coffee" })}>{t("cart.goToCatalog")}</button>} />
        </main>
      </>
    );
  }

  const keys: string[] = Array.from(new Set<string>(products.flatMap((p) => Object.keys(p.attributes ?? {}))));
  const row = (label: string, cells: React.ReactNode[]) => (
    <div className="cmp-row" key={label}>
      <span className="cmp-label">{label}</span>
      {cells.map((c, i) => (
        <span key={i}>{c}</span>
      ))}
    </div>
  );

  return (
    <>
      <TopBar title={t("compare.title")} onBack={goBack} />
      <main className="page page--no-nav">
        <div className="compare">
          {products.map((p) => (
            <div key={p.id} className="compare__col">
              <button className="compare__frame" style={{ background: `var(--tone-${toneFor(p.id)})` }} onClick={() => navigate({ name: "product", slug: p.slug })}>
                <ProductImage src={p.images[0]} alt={p.name} category={p.category} className="product-card__image" />
              </button>
              <div className="compare__name">{p.name}</div>
              <button className="link-button" onClick={() => compare.remove(p.id)}>
                {t("compare.remove")}
              </button>
            </div>
          ))}
        </div>
        {row(t("compare.price"), products.map((p) => `${formatUzs(p.variants[0].priceUzs, locale)} · ${p.variants[0].label}`))}
        {keys.map((k) => row(k, products.map((p) => p.attributes?.[k] ?? "—")))}
        {row(t("compare.rating"), products.map((p) => (p.rating ? `★ ${p.rating.toFixed(1)} · ${p.reviewsCount ?? 0}` : "—")))}
        {row(t("compare.about"), products.map((p) => <span className="cmp-about">{p.shortDescription}</span>))}
        <div className="compare" style={{ marginTop: 16 }}>
          {products.map((p) => {
            const v = p.variants.find((x) => x.stock > 0);
            return (
              <button
                key={p.id}
                className="btn btn--ghost"
                disabled={!v || p.orderMode !== "buy_now"}
                onClick={(e) => {
                  if (!v) return;
                  flyToCart(e.currentTarget.closest("main")?.querySelectorAll(".compare__frame")[products.indexOf(p)] ?? null);
                  addItem(p.id, v.id);
                  haptic("success");
                }}
              >
                {v ? t("product.addToCart") : t("product.outOfStockShort")}
              </button>
            );
          })}
        </div>
      </main>
    </>
  );
}
